import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tabs,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Progress,
  Modal,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  UserAddOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import apiService from "services/api/apiService";
import OnboardingForm from '../../components/Onboarding/OnboardingForm';
import OnboardingDetails from '../../components/Onboarding/OnboardingDetails';
import BuddyMentorship from '../../components/Onboarding/BuddyMentorship';

const { Title } = Typography;

interface OnboardingProcess {
  id: number;
  tenant_id: string;
  employee_id: number;
  employee_name: string;
  department: string;
  position: string;
  onboarding_status: 'pending' | 'in_progress' | 'completed' | 'on_hold';
  start_date: string;
  completion_percentage: string;
  buddy_assigned_id?: number;
  buddy_name?: string;
  days_since_start: number;
  task_summary: {
    total_tasks: number;
    completed_tasks: number;
    overdue_tasks: number;
    pending_overdue: number;
  };
  created_at: string;
  updated_at: string;
}

const EmployeeOnboarding: React.FC = () => {
  const [onboardingProcesses, setOnboardingProcesses] = useState<OnboardingProcess[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<OnboardingProcess | null>(null);
  const [editingProcess, setEditingProcess] = useState<OnboardingProcess | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'onboarding' | 'mentorship'>('onboarding');

  useEffect(() => {
    apiService.getEmployees().then(r => { if (r.success) setEmployees(r.data || []); });
  }, []);

  const fetchOnboardingData = async (updatedId?: number) => {
    try {
      setLoading(true);
      const response = await apiService.getOnboardingProcesses();
      
      if (response.success) {
        let onboardingData = response.data || [];
        
        // Ensure it's always an array
        if (!Array.isArray(onboardingData)) {
          if (onboardingData.processes) {
            onboardingData = onboardingData.processes;
          } else if (onboardingData.data) {
            onboardingData = onboardingData.data;
          } else {
            onboardingData = [];
          }
        }
        
        setOnboardingProcesses(Array.isArray(onboardingData) ? onboardingData : []);
      } else {
        setOnboardingProcesses([]);
      if (updatedId) { const updated = [].find((p: any) => p.id === updatedId); if (updated) setSelectedProcess(updated); }
      }
    } catch (error) {
      console.error('Onboarding fetch error:', error);
      message.error('Failed to fetch onboarding data');
      setOnboardingProcesses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnboardingData();
  }, []);

  // Calculate statistics
  const stats = {
    total: onboardingProcesses.length,
    pending: onboardingProcesses.filter(p => p.onboarding_status === 'pending').length,
    inProgress: onboardingProcesses.filter(p => p.onboarding_status === 'in_progress').length,
    completed: onboardingProcesses.filter(p => p.onboarding_status === 'completed').length,
    avgCompletion: onboardingProcesses.length > 0 
      ? (onboardingProcesses.reduce((sum, p) => sum + parseFloat(p.completion_percentage || '0'), 0) / onboardingProcesses.length).toFixed(1)
      : 0
  };

  // Handle CRUD operations
  const handleAddOnboarding = () => {
    setEditingProcess(null);
    setIsFormModalVisible(true);
  };

  const handleEditOnboarding = (process: OnboardingProcess) => {
    setEditingProcess(process);
    setIsFormModalVisible(true);
  };

  const handleViewOnboarding = (process: OnboardingProcess) => {
    setSelectedProcess(process);
    setIsDetailsModalVisible(true);
  };

  const handleDeleteOnboarding = async (processId: number) => {
    try {
      const response = await apiService.deleteOnboarding(processId.toString());
      if (response.success) {
        message.success("Onboarding process deleted successfully");
        fetchOnboardingData();
      } else {
        message.error(response.error || "Failed to delete onboarding process");
      }
    } catch (error) {
      message.error('Failed to delete onboarding process');
    }
  };

  const handleExportOnboarding = async () => {
    try {
      const response = await apiService.exportOnboarding();
      if (response.success) {
        const exportData = JSON.stringify(response.data, null, 2);
        const blob = new Blob([exportData], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `onboarding-report-${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success("Onboarding report exported successfully");
      } else {
        message.error(response.error || "Failed to export onboarding report");
      }
    } catch (error) {
      message.error("Failed to export onboarding report");
    }
  };

  const handleFormSuccess = async () => {
    setIsFormModalVisible(false);
    const editedId = editingProcess?.id;
    setEditingProcess(null);
    await fetchOnboardingData();
    // Update selectedProcess if details modal is open for the edited record
    if (editedId) {
      setOnboardingProcesses(prev => {
        const updated = prev.find((p: any) => p.id === editedId);
        if (updated) setSelectedProcess(updated);
        return prev;
      });
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'default';
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'processing';
      case 'pending': return 'warning';
      case 'on_hold': return 'error';
      default: return 'default';
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return '#52c41a';
    if (percentage >= 50) return '#faad14';
    if (percentage >= 25) return '#1890ff';
    return '#ff4d4f';
  };

  const formatStatus = (status: string) => {
    if (!status) return 'UNKNOWN';
    return status.replace('_', ' ').toUpperCase();
  };

  const onboardingColumns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (record: OnboardingProcess) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.employee_name || 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.department || 'N/A'} • {record.position || 'N/A'}
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'onboarding_status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {formatStatus(status)}
        </Tag>
      )
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (record: OnboardingProcess) => {
        const percentage = parseFloat(record.completion_percentage || '0');
        return (
          <div style={{ width: '120px' }}>
            <Progress
              percent={percentage}
              size="small"
              strokeColor={getProgressColor(percentage)}
              format={(percent) => `${percent}%`}
            />
          </div>
        );
      }
    },
    {
      title: 'Buddy',
      key: 'buddy',
      render: (record: OnboardingProcess) => (
        record.buddy_name ? (
          <Tag color="blue" icon={<TeamOutlined />}>
            {record.buddy_name}
          </Tag>
        ) : (
          <Tag color="default">Not Assigned</Tag>
        )
      )
    },
    {
      title: 'Tasks',
      key: 'tasks',
      render: (record: OnboardingProcess) => {
        const taskSummary = record.task_summary || {
          total_tasks: 0,
          completed_tasks: 0,
          overdue_tasks: 0,
          pending_overdue: 0
        };
        
        return (
          <div>
            <Space>
              <Tag color="green" icon={<CheckCircleOutlined />}>
                {taskSummary.completed_tasks}
              </Tag>
              <Tag color="blue">
                {taskSummary.total_tasks} Total
              </Tag>
              {taskSummary.overdue_tasks > 0 && (
                <Tag color="red" icon={<ExclamationCircleOutlined />}>
                  {taskSummary.overdue_tasks} Overdue
                </Tag>
              )}
            </Space>
          </div>
        );
      }
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (date: string, record: OnboardingProcess) => (
        <div>
          <div>{date ? new Date(date).toLocaleDateString() : 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.days_since_start || 0} days ago
          </div>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: OnboardingProcess) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewOnboarding(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Process">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditOnboarding(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this onboarding process?"
            onConfirm={() => handleDeleteOnboarding(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Process">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header with Statistics */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Employee Onboarding
            </Title>
            <p style={{ color: '#666', margin: 0 }}>
              Manage employee onboarding processes and buddy system
            </p>
          </div>
          
          <Space>
            <Button
              icon={<FileTextOutlined />}
              onClick={handleExportOnboarding}
            >
              Export Reports
            </Button>
          </Space>
        </div>

        <Row gutter={16}>
          <Col span={4}>
            <Statistic
              title="Total Processes"
              value={stats.total}
              prefix={<UserAddOutlined style={{ color: '#1890ff' }} />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Pending"
              value={stats.pending}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="In Progress"
              value={stats.inProgress}
              prefix={<ExclamationCircleOutlined style={{ color: '#1890ff' }} />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Completed"
              value={stats.completed}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Avg Completion"
              value={`${stats.avgCompletion}%`}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
            />
          </Col>
        </Row>
      </Card>

      {/* Onboarding Processes Table */}
      <Card 
        title="Onboarding Processes"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAddOnboarding}
          >
            Start New Onboarding
          </Button>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'onboarding' | 'mentorship')}
          items={[
            {
              key: 'onboarding',
              label: '📋 Onboarding Processes',
              children: (
                <Table
                  columns={onboardingColumns}
                  dataSource={onboardingProcesses}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} onboarding processes`,
                  }}
                  scroll={{ x: 1200 }}
                />
              )
            },
            {
              key: 'mentorship',
              label: '🤝 Buddy Mentorship',
              children: (
                <BuddyMentorship employees={employees} />
              )
            }
          ]}
        />
      </Card>

      {/* Onboarding Form Modal */}
      <Modal
        title={editingProcess ? 'Edit Onboarding Process' : 'Start New Onboarding'}
        open={isFormModalVisible}
        onCancel={() => setIsFormModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <OnboardingForm
          process={editingProcess}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsFormModalVisible(false)}
        />
      </Modal>

      {/* Onboarding Details Modal */}
      <Modal
        title="Onboarding Process Details"
        open={isDetailsModalVisible}
        onCancel={() => setIsDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetailsModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => {
              setIsDetailsModalVisible(false);
              handleEditOnboarding(selectedProcess!);
            }}
          >
            Edit Process
          </Button>
        ]}
        width={1000}
        destroyOnClose
      >
        {selectedProcess && (
          <OnboardingDetails 
            process={selectedProcess} 
            onRefresh={fetchOnboardingData}
          />
        )}
      </Modal>
    </div>
  );
};

export default EmployeeOnboarding;
